import React, { memo, useMemo, useRef } from 'react';
import { usePopper } from 'react-popper';

import { Box, CloseButton, Portal, useChakraContext } from '@chakra-ui/react';

import { VirtualElement } from '@popperjs/core/lib/types';

import { IconsLinear } from '@nocowanie/icons';

import './../date-picker/date-picker.scss';
import { RangePickerPopperProps } from './range-picker-popper.props';

export const RangePickerPopper = memo(
    ({
        translationData,
        referenceBounds,
        isMobile = false,
        isPopoverOpen = false,
        tooltipText,
        onClose,
    }: RangePickerPopperProps) => {
        const [popperElement, setPopperElement] = React.useState<HTMLElement | null>(null);
        const arrowRef = useRef<HTMLDivElement | null>(null);
        const referenceElement = useMemo(
            () =>
                ({
                    getBoundingClientRect: () => referenceBounds,
                }) as VirtualElement,
            [referenceBounds],
        );
        const { styles, attributes } = usePopper(referenceElement, popperElement, {
            placement: 'top',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [12, 12],
                    },
                },
                {
                    name: 'preventOverflow',
                    options: {
                        padding: 10,
                    },
                },
                {
                    name: 'arrow',
                    options: {
                        padding: 10,
                        element: arrowRef?.current,
                    },
                },
            ],
            // onFirstUpdate: (state) => {} // we can't use it - see https://github.com/floating-ui/react-popper/issues/448
        });
        const theme = useChakraContext();
        const {
            // popper: popoverStyles,
            content: popoverContentStyles,
            body: popoverBodyStyles,
        } = theme?.getSlotRecipe('popover').base || {};

        return isPopoverOpen && tooltipText ? (
            <Portal>
                <Box
                    ref={setPopperElement}
                    css={{
                        ...styles.popper,
                        zIndex: 'popover',
                        pointerEvents: 'auto',
                    }}
                    {...attributes.popper}
                >
                    <Box
                        css={{
                            ...popoverContentStyles,
                            '--popover-bg': '{colors.gray.950}',
                            color: 'white',
                            borderColor: 'transparent',
                            maxW: '240px',
                            w: 'auto',
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 2,
                            p: 3,
                        }}
                    >
                        <Box
                            ref={arrowRef}
                            css={{
                                width: '10px',
                                height: '10px',
                                backgroundColor: 'inherit',
                                position: 'absolute',
                                bottom: '-5px',
                                transform: `${styles.arrow.transform} rotate(45deg)`,
                            }}
                            {...attributes.arrow}
                        />
                        {isMobile ? (
                            <CloseButton
                                aria-label={translationData.tooltipClose}
                                size={'sm'}
                                color={'white'}
                                css={{
                                    order: 1,
                                }}
                                onClick={onClose}
                                alignSelf={'start'}
                                mt={-2}
                                mr={-2}
                            >
                                <IconsLinear.Close />
                            </CloseButton>
                        ) : null}
                        <Box
                            css={{
                                ...popoverBodyStyles,
                                fontSize: 'xs',
                            }}
                        >
                            {tooltipText}
                        </Box>
                    </Box>
                </Box>
            </Portal>
        ) : null;
    },
);
